import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { useRegisterMutation } from '../../store/api/authApi';
import { setPendingAuth } from '../../store/slices/authSlice';
import GoogleSignInButton from '../common/GoogleSignInButton';
import Turnstile, { isTurnstileConfigured } from '../common/Turnstile';
import AuthShell, { AuthField, AuthSubmit, AuthAlert, AuthTabs } from './AuthShell';

const Register = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [register, { isLoading }] = useRegisterMutation();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [acceptMarketing, setAcceptMarketing] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const turnstileRef = useRef(null);

  // The Turnstile token is single use, so drop it and re-arm the widget
  // whenever a submit does not go through.
  const resetCaptcha = () => {
    setCaptchaToken('');
    turnstileRef.current?.reset();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Must contain uppercase, lowercase, and number';
    }

    if (!acceptTerms) {
      newErrors.terms = 'You must accept the Terms of Service';
    }

    if (isTurnstileConfigured() && !captchaToken) {
      newErrors.captcha = 'Please complete the security check';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError('');

    if (!validate()) return;

    try {
      await register({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        turnstileToken: captchaToken,
      }).unwrap();

      dispatch(setPendingAuth({ email: formData.email, type: 'registration' }));
      navigate('/verify-otp');
    } catch (err) {
      setApiError(err.data?.message || 'Registration failed. Please try again.');
      resetCaptcha();
    }
  };

  return (
    <AuthShell
      width="wide"
      title="Create your merchant account"
      subtitle="Takes under a minute. No card required to get started."
      panelHeading={<>Start taking <em>crypto payments</em> today.</>}
      panelLead="No KYC to get started. Settle straight to your own wallet."
    >
      <AuthTabs active="signup" />
      {apiError && <AuthAlert>{apiError}</AuthAlert>}

      <form onSubmit={handleSubmit} noValidate>
        <div className="mp-auth-fields">
          <div className="mp-auth-row">
            <AuthField
              id="firstName"
              name="firstName"
              type="text"
              label="First name"
              placeholder="Jane"
              autoComplete="given-name"
              value={formData.firstName}
              onChange={handleChange}
              error={errors.firstName}
            />
            <AuthField
              id="lastName"
              name="lastName"
              type="text"
              label="Last name"
              placeholder="Okafor"
              autoComplete="family-name"
              value={formData.lastName}
              onChange={handleChange}
              error={errors.lastName}
            />
          </div>

          <AuthField
            id="email"
            name="email"
            type="email"
            label="Work email"
            placeholder="you@company.com"
            autoComplete="email"
            value={formData.email}
            onChange={handleChange}
            error={errors.email}
          />

          <AuthField
            id="password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            label="Password"
            placeholder="••••••••"
            autoComplete="new-password"
            value={formData.password}
            onChange={handleChange}
            error={errors.password}
            hint="At least 8 characters, with an uppercase letter, a lowercase letter and a number."
            trailing={
              <button
                type="button"
                className="mp-auth-eye"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            }
          />
        </div>

        <div className="mp-auth-consents">
          <label className="mp-auth-check" htmlFor="marketing">
            <input
              type="checkbox"
              id="marketing"
              checked={acceptMarketing}
              onChange={(e) => setAcceptMarketing(e.target.checked)}
            />
            <span>Yes, I would like to subscribe to marketing and promotional updates.</span>
          </label>

          <label className="mp-auth-check" htmlFor="terms">
            <input
              type="checkbox"
              id="terms"
              checked={acceptTerms}
              aria-invalid={Boolean(errors.terms)}
              aria-describedby={errors.terms ? 'terms-err' : undefined}
              onChange={(e) => {
                setAcceptTerms(e.target.checked);
                setErrors((prev) => ({ ...prev, terms: '' }));
              }}
            />
            <span>
              I agree with MaxelPay's <a href="/legal/terms">Partners Terms of Service</a> and{' '}
              <a href="/legal/privacy">Privacy Policy</a>.
            </span>
          </label>
          {errors.terms && (
            <span className="mp-auth-err" id="terms-err">{errors.terms}</span>
          )}
        </div>

        <Turnstile
          ref={turnstileRef}
          action="register"
          onVerify={(token) => {
            setCaptchaToken(token);
            setErrors((prev) => ({ ...prev, captcha: '' }));
          }}
          onExpire={() => setCaptchaToken('')}
          onError={() => setCaptchaToken('')}
        />
        {errors.captcha && <span className="mp-auth-err">{errors.captcha}</span>}

        <div style={{ marginTop: 18 }}>
          <AuthSubmit label="Create account" loading={isLoading} />
        </div>
      </form>

      <div className="mp-auth-or">Or continue with</div>

      {/* Google's own button, rendered for real rather than hidden under a
          lookalike. The duplicate styled button that used to sit here was what
          you could see while Google's smaller one took the clicks, so the two
          never lined up. */}
      <div className="rd-social-btn-wrap">
        <GoogleSignInButton onError={setApiError} />
      </div>

      <p className="mp-auth-foot">
        Already have an account? <Link to="/login">Sign in</Link>
      </p>
    </AuthShell>
  );
};

export default Register;
